import React, { useEffect, useState } from 'react';
// eslint-disable-next-line no-restricted-imports -- NiceModal -> overlay-kit migration is out of scope for this PR
import NiceModal from '@ebay/nice-modal-react';
import { DehydratedState, Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { RecoilRoot } from 'recoil';
import '@zep-us/zus/styles';
import '../styles/play.globals.scss';
import 'react-lazy-load-image-component/src/effects/blur.css';
import '../init';
import { defaultAxiosConfig, ZepApiProvider } from '@zep/apis';
import { AuthProvider } from '@zep/auth';
import { logger } from '@zep/core-logger';
import { DialogProvider } from '@zep/dialog';
import { DevToolingMount } from '@zep/internal';
import { EquipmentCatalogBootstrapper } from '@zep/play';
import type { PageWithLayout, User } from '@zep/types';
import { isMobile } from '@zep/utils';
import { GlobalErrorBoundary } from '../components/GlobalErrorBoundary';
import { PlayAppLoggerInitializer } from '../components/PlayAppLoggerInitializer';

// design mode 전용 인스펙터. next/dynamic + ssr:false 로 별도 청크 분리해
// PRD 초기 번들에서 제외(렌더는 아래 NEXT_PUBLIC_STAGE 가드).
const DesignInspector = dynamic(() => import(/* webpackChunkName: "design-inspector" */'@zep/internal').then(m => ({
  default: m.DesignInspector
})), {
  ssr: false
});
function PlayApp({
  Component,
  pageProps
}: PlayAppProps) {
  const {
    serverUser
  } = pageProps;
  const router = useRouter();
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        useErrorBoundary: true,
        networkMode: 'always'
      },
      mutations: {
        networkMode: 'always'
      }
    }
  }));
  const getLayout = Component.getLayout ?? (page => page);
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_STAGE === 'LOC') return;
    const {
      hostname,
      pathname
    } = window.location;
    if (!hostname.endsWith('.zep.us') && hostname !== 'zep.us') {
      window.location.href = `https://zep.us${pathname}`;
    }
  }, []);
  useEffect(() => {
    // basePath('/play')를 포함한 실제 URL 경로를 기록하기 위해
    // router.asPath(basePath 제거됨) 대신 window.location.pathname 사용.
    const track = () => {
      logger.track('page_view', {
        location: window.location.pathname
      });
    };
    track();
    router.events.on('routeChangeComplete', track);
    return () => router.events.off('routeChangeComplete', track);
  }, [router]);
  return <GlobalErrorBoundary data-sentry-element="GlobalErrorBoundary" data-sentry-component="PlayApp" data-sentry-source-file="_app.tsx">
      {process.env.NEXT_PUBLIC_STAGE !== 'PRD' && <DesignInspector />}
      <DevToolingMount routeMapHashId={Array.isArray(router.query.mapHashId) ? router.query.mapHashId[0] : router.query.mapHashId} data-sentry-element="DevToolingMount" data-sentry-source-file="_app.tsx" />
      <PlayAppLoggerInitializer data-sentry-element="PlayAppLoggerInitializer" data-sentry-source-file="_app.tsx" />
      <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.tsx">
        <RecoilRoot data-sentry-element="RecoilRoot" data-sentry-source-file="_app.tsx">
          <Hydrate state={pageProps.dehydratedState} data-sentry-element="Hydrate" data-sentry-source-file="_app.tsx">
            <ZepApiProvider axiosConfig={defaultAxiosConfig} data-sentry-element="ZepApiProvider" data-sentry-source-file="_app.tsx">
              <AuthProvider initialUser={serverUser} data-sentry-element="AuthProvider" data-sentry-source-file="_app.tsx">
                <NiceModal.Provider data-sentry-element="unknown" data-sentry-source-file="_app.tsx">
                  <DialogProvider data-sentry-element="DialogProvider" data-sentry-source-file="_app.tsx">
                    <EquipmentCatalogBootstrapper data-sentry-element="EquipmentCatalogBootstrapper" data-sentry-source-file="_app.tsx" />
                    {getLayout(<Component {...pageProps} />)}
                  </DialogProvider>
                </NiceModal.Provider>
              </AuthProvider>
            </ZepApiProvider>
            {!isMobile() && <ReactQueryDevtools initialIsOpen={false} position={'bottom-right'} />}
          </Hydrate>
        </RecoilRoot>
      </QueryClientProvider>
    </GlobalErrorBoundary>;
}
export default PlayApp;
type CommonPageProps = {
  serverUser?: User;
  dehydratedState?: DehydratedState;
};
type PlayAppProps = AppProps<CommonPageProps> & {
  Component: PageWithLayout<CommonPageProps>;
};